import type { CSSProperties, FC, ReactNode } from 'react'

import { useMemo } from 'react'
import { cn } from '@/lib/utils'
import { TweenNumber } from '@/components/generals/tween-number'

export interface TreemapItem {
    name: string
    share: number
}

interface TreemapProps {
    title: string
    items: Array<TreemapItem>
    run: boolean
    aspect?: number
    className?: string
}

interface Rect {
    x: number
    y: number
    w: number
    h: number
}

const FILLS = [0.2, 0.13, 0.085, 0.055, 0.035, 0.025, 0.02]

const worst = (row: Array<number>, side: number): number => {
    const sum = row.reduce((total, value) => total + value, 0)
    const max = Math.max(...row)
    const min = Math.min(...row)

    return Math.max((side * side * max) / (sum * sum), (sum * sum) / (side * side * min))
}

/**
 * Squarified treemap layout in an arbitrary box.
 */
const squarify = (areas: Array<number>, box: Rect): Array<Rect> => {
    const out: Array<Rect> = []
    let rest = { ...box }
    let i = 0

    while (i < areas.length) {
        const side = Math.min(rest.w, rest.h)
        const row = [areas[i]]
        let j = i + 1

        while (j < areas.length && worst([...row, areas[j]], side) <= worst(row, side)) {
            row.push(areas[j])
            j++
        }

        const sum = row.reduce((total, value) => total + value, 0)

        if (rest.w >= rest.h) {
            const width = sum / rest.h
            let y = rest.y

            for (const area of row) {
                const height = area / width

                out.push({ x: rest.x, y, w: width, h: height })
                y += height
            }

            rest = { x: rest.x + width, y: rest.y, w: rest.w - width, h: rest.h }
        }
        else {
            const height = sum / rest.w
            let x = rest.x

            for (const area of row) {
                const width = area / height

                out.push({ x, y: rest.y, w: width, h: height })
                x += width
            }

            rest = { x: rest.x, y: rest.y + height, w: rest.w, h: rest.h - height }
        }

        i = j
    }

    return out
}

/**
 * A materials treemap that grows out of one small square into its squarified layout,
 * with each share counting up as its cell opens.
 */
export const Treemap: FC<TreemapProps> = ({ title, items, run, aspect = 4 / 3, className }): ReactNode => {
    const sorted = useMemo(() => [...items].filter(item => item.share > 0).sort((a, b) => b.share - a.share), [items])

    const rects = useMemo(() => {
        const width = 100 * aspect
        const height = 100
        const total = sorted.reduce((sum, item) => sum + item.share, 0) || 1
        const areas = sorted.map(item => (item.share / total) * width * height)

        return squarify(areas, { x: 0, y: 0, w: width, h: height }).map(rect => ({
            left: (rect.x / width) * 100,
            top: (rect.y / height) * 100,
            width: (rect.w / width) * 100,
            height: (rect.h / height) * 100
        }))
    }, [sorted, aspect])

    const seed = 22
    const seedWidth = seed / aspect

    return (
        <div className={cn('w-full', className)}>
            <div className='relative w-full overflow-hidden' style={{ aspectRatio: String(aspect) }} aria-hidden='true'>
                {sorted.map((item, index) => {
                    const rect = rects[index]
                    const style: CSSProperties = run
                        ? {
                            left: `${rect.left}%`,
                            top: `${rect.top}%`,
                            width: `${rect.width}%`,
                            height: `${rect.height}%`,
                            transitionDelay: `${index * 110}ms`
                        }
                        : {
                            left: '0%',
                            top: `${100 - seed}%`,
                            width: `${seedWidth}%`,
                            height: `${seed}%`,
                            transitionDelay: '0ms'
                        }

                    return (
                        <div
                            key={item.name}
                            className='treemap-cell absolute'
                            style={style}
                        >
                            <div
                                className='absolute inset-0 border border-background'
                                style={{ backgroundColor: `rgba(236, 230, 216, ${FILLS[index] ?? 0.02})` }}
                            />

                            <div className={cn('relative flex h-full flex-col justify-between overflow-hidden p-2.5 transition-opacity duration-500', run ? 'opacity-100' : 'opacity-0')} style={{ transitionDelay: run ? `${index * 110 + 420}ms` : '0ms' }}>
                                <span className='hud truncate text-ink'>
                                    {item.name}
                                </span>

                                <span className='font-heading text-[clamp(16px,1.8vw,26px)] font-medium leading-none tracking-[-0.02em] text-ink'>
                                    <TweenNumber value={run ? item.share : 0} duration={1100} suffix='%' />
                                </span>
                            </div>
                        </div>
                    )
                })}
            </div>

            <table className='sr-only'>
                <caption>
                    {title}
                </caption>

                <thead>
                    <tr>
                        <th scope='col'>
                            Material
                        </th>

                        <th scope='col'>
                            Share
                        </th>
                    </tr>
                </thead>

                <tbody>
                    {sorted.map(item => (
                        <tr key={item.name}>
                            <th scope='row'>
                                {item.name}
                            </th>

                            <td>
                                {item.share}%
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}
